import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
    Button,
    TextField,
    Box,
    Typography,
    Chip,
    Divider,
    Alert,
    CircularProgress,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { adminGetUser, adminUpdateUser, UserDetail } from '../../../admin.users.api';
import { UserData } from '../../../../../utils';
import StripeLink from '../../pods/components/StripeLink';

interface EditUserDialogProps {
    open: boolean;
    user: UserData | null;
    onClose: () => void;
    onSaved: (user: UserData) => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[\d\s\-()]+$/;

const EditUserDialog: React.FC<EditUserDialogProps> = ({ open, user, onClose, onSaved }) => {
    const [detail, setDetail] = useState<UserDetail | null>(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !user) {
            return;
        }
        setLoading(true);
        setError('');
        adminGetUser(user.uuid)
            .then((response) => {
                setDetail(response.data);
                setEmail(response.data.user.email);
                setPhone(response.data.user.phone || '');
            })
            .catch(() => setError('Failed to load user details'))
            .finally(() => setLoading(false));
    }, [open, user]);

    const handleClose = () => {
        setDetail(null);
        setEmail('');
        setPhone('');
        setFormErrors({});
        setConfirmOpen(false);
        setError('');
        onClose();
    };

    const handleSaveClick = () => {
        const errors: { [key: string]: string } = {};

        if (!email || !EMAIL_REGEX.test(email)) {
            errors.email = 'Valid email is required';
        }

        if (phone && !PHONE_REGEX.test(phone)) {
            errors.phone = 'Invalid phone number format';
        }

        setFormErrors(errors);

        if (Object.keys(errors).length > 0) {
            return;
        }

        setConfirmOpen(true);
    };

    const handleConfirmSave = async () => {
        if (!detail) {
            return;
        }
        setSaving(true);
        setError('');
        try {
            const response = await adminUpdateUser(detail.user.uuid, {
                email: email.trim(),
                phone: phone.trim() || undefined,
            });
            setConfirmOpen(false);
            onSaved(response.data.user);
            handleClose();
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update user');
            setConfirmOpen(false);
        } finally {
            setSaving(false);
        }
    };

    const emailChanged = detail && email.trim() !== detail.user.email;
    const phoneChanged = detail && phone.trim() !== (detail.user.phone || '');

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Edit User Login Info</DialogTitle>
                <DialogContent>
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}
                    {!loading && detail && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Box>
                                <Typography variant="h6">
                                    {detail.user.first_name} {detail.user.last_name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    @{detail.user.user_name} · {detail.user.uuid}
                                </Typography>
                            </Box>

                            <Divider />

                            <TextField
                                label="Email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                error={!!formErrors.email}
                                helperText={
                                    formErrors.email ||
                                    (emailChanged
                                        ? 'Changing this will require the user to re-verify their new email'
                                        : detail.user.email_verified_at
                                        ? 'Verified'
                                        : 'Not verified')
                                }
                                fullWidth
                                size="small"
                            />

                            <TextField
                                label="Phone"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                error={!!formErrors.phone}
                                helperText={
                                    formErrors.phone ||
                                    (phoneChanged
                                        ? 'Changing this will require the user to re-verify their new phone number'
                                        : detail.user.phone_verified_at
                                        ? 'Verified'
                                        : 'Not verified')
                                }
                                fullWidth
                                size="small"
                                placeholder="+1 (555) 123-4567"
                            />

                            <Divider />

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Stripe
                                </Typography>
                                {detail.stripe_customer_id ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography variant="body2">
                                            {detail.stripe_customer_id}
                                        </Typography>
                                        <StripeLink
                                            id={detail.stripe_customer_id}
                                            entity="customers"
                                        />
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No Stripe customer on file
                                    </Typography>
                                )}
                            </Box>

                            <Box>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Associated Athletes ({detail.individuals.length})
                                </Typography>
                                {detail.individuals.length > 0 ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {detail.individuals.map((individual) => (
                                            <Chip
                                                key={individual.uuid}
                                                label={`${individual.name}${
                                                    individual.organization_name
                                                        ? ` (${individual.organization_name})`
                                                        : ''
                                                }`}
                                            />
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No athletes associated with this user
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        onClick={handleSaveClick}
                        variant="contained"
                        color="primary"
                        disabled={loading || !detail || (!emailChanged && !phoneChanged)}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={confirmOpen}
                onClose={() => setConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberIcon color="warning" />
                    Confirm Login Info Change
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Please double check the new information below before saving:
                    </DialogContentText>
                    {emailChanged && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>New Email:</strong> {email}
                        </Typography>
                    )}
                    {phoneChanged && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>New Phone:</strong> {phone}
                        </Typography>
                    )}
                    <Alert severity="warning">
                        Make sure this email/phone number is correct, and that the user confirms
                        they have access to it. This change takes effect immediately and cannot be
                        undone from this screen.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmSave}
                        variant="contained"
                        color="warning"
                        disabled={saving}
                    >
                        {saving ? 'Saving...' : 'Confirm & Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default EditUserDialog;
